import type { MetaDataSchema } from '@mimir/common/mdf/metadataSchema';
import type { DefaultFilters, Filter } from '@mimir/common/filters/Filters';
import type { ActionContext } from 'vuex';
import type { StoreType } from '../store/state';
import { MEDIA_FILTER, MediaTypes, filters, setRadioFilter, setSelectedMediaTypes, NonMediaTypes, type SearchCategoryUI, ALL_FILTER } from '../globals/filter';
import type { SearchCategory, SearchCategoryCount } from '@mimir/common/dto/searchModels';
import type { Route } from 'vue-router';
import { getFiltersFromSearchString } from '@mimir/common/filters/filter-util';
import type VueRouter from 'vue-router';
import { ItemType } from '@mimir/common/dto/itemTypes';
import { MediaAvailabilityStatus } from '@mimir/common/dto/mediaDtos';
import { getI18n } from '@mimir/common/i18n/initializeI18n';
import { canCreateFolder, canCreatePerson, canManageLiveRecording } from '@mimir/common/util/security';
import { assertUnreachable } from '@mimir/common/util/general';
import { FOLDER_DEFAULT_MDF_ID } from '@mimir/common/mdf/mdfDefaults';


export type SetDateFilterFieldPayload = { key: string, label: string, mdf: MetaDataSchema | undefined };

export function setDateFilterField(context: ActionContext<any, any>, payload: SetDateFilterFieldPayload | undefined) {
  context.commit('setDateFilterField', payload);
}

export function clearTypeFilter(filterRemoved: Filter<any>, store: StoreType) {
  // Type filter values are combined into a single chip
  filterRemoved.clearValue();
  // When item filter type is removed, set the radio filter to all.
  // if there is no media item types are selected, set all media item types and show them as disabled.
  store.commit('setAllTypesFilter', true);
}

export function updateMediaTypeFilters(types: SearchCategory[]) {
  const categories: SearchCategory[] = [];
  types.forEach((type) => {
    if (isMediaType(type)) {
      categories.push(type);
    }
  });
  if (!categories.length) return;
  setSelectedMediaTypes(categories);
}

export function isMediaType(type: any): type is typeof MediaTypes[number] {
  return MediaTypes.includes(type);
}

export function isNonMediaType(type: any): type is typeof NonMediaTypes[number] {
  return NonMediaTypes.includes(type);
}

export function hasOnlyNonMediaTypes(selected: SearchCategory[]): boolean {
  return !selected.some(category => isMediaType(category));
}

// Get search category directly from route query, useful for bookmarks because state filter object is not loaded yet.
export function getItemTypesAndFolderIdFromSearchString(route: Route): SearchCategory[] {
  const searchString = Array.isArray(route.query.searchString) ? route.query.searchString[0] : route.query.searchString;
  const searchTerms = searchString ? getFiltersFromSearchString(searchString) : [];

  const formIdFilter = searchTerms?.find(term => term.startsWith('type:'));
  const searchCategory = formIdFilter ? formIdFilter.replace('type:', '').split(',') : [];

  return searchCategory as SearchCategory[];
}

export function hasBothMediaAndOtherTypes(selected: SearchCategory[]): boolean {
  const hasMediaTypes = selected.some(isMediaType);
  const hasOtherTypes = selected.some(category => !isMediaType(category));
  return hasMediaTypes && hasOtherTypes;
}

/**
 * Set initial filters when loading mimir with no type filter.
 */
export const applyMediaTypeFilters = (mediaTypes: SearchCategory[], store: StoreType, router: VueRouter) => {
  const defaultFilters = store.getters.filters.defaultFilters as DefaultFilters;
  mediaTypes.forEach((type) => {
    defaultFilters['filter:itemType'].addValue(type);
  });
  const typeFilterValue = defaultFilters['filter:itemType'].serializedValue;
  const query = router.currentRoute.query;
  const searchString = query.searchString ? typeFilterValue + ' ' + query.searchString : typeFilterValue;
  void router.push({
      name: router.currentRoute.name ?? 'searchItem',
      query: {...query, searchString: searchString},
  });
};

export const applyDefaultAllFilter = (router: VueRouter) => {
  const query = router.currentRoute.query;
  void router.push({
    name: router.currentRoute.name ?? 'searchItem',
    query: {...query, allTypes: 'true'},
  });
};

export const sanitizeFilterForFolderType = (newRadioFilter: SearchCategoryUI, oldRadioFilter: SearchCategoryUI | null, store: StoreType) => {
  const showFolderMetadata = store.getters.featureFlags.showFolderMetadataEditor as boolean;
  if (!showFolderMetadata) return;
  const defaultFilters = store.getters.filters.defaultFilters as DefaultFilters;
  if (newRadioFilter === 'folder') {
    defaultFilters['filter:formId'].clearValue();
    defaultFilters['filter:formId'].addValue(FOLDER_DEFAULT_MDF_ID);
  } else if (oldRadioFilter === 'folder') {
    defaultFilters['filter:formId'].clearValue();
  }
};

export const getUploadIcon = (type: SearchCategory) => {
  switch (type) {
    case ItemType.IMAGE: return 'mdi-tray-arrow-up';
    case ItemType.AUDIO: return 'mdi-tray-arrow-up';
    case ItemType.VIDEO: return 'mdi-tray-arrow-up';
    case ItemType.FILE: return 'mdi-tray-arrow-up';
    case ItemType.PERSON: return 'add';
    case ItemType.FOLDER: return 'add';
    case ItemType.CLIP_LIST: return '';
    case MediaAvailabilityStatus.STREAMING: return 'add';
    default: return '';
  }
};

export const getTooltip = (type: SearchCategory, isSelected: boolean) => {
  const prefix = getI18n().t('SearchMenuItem.Show');
  switch (type) {
    case ItemType.VIDEO: return getI18n().t('SearchMenuItem.Videos', {prefix});
    case ItemType.IMAGE: return getI18n().t('SearchMenuItem.Images', {prefix});
    case ItemType.PERSON: return getI18n().t('SearchMenuItem.Persons', {prefix});
    case ItemType.AUDIO: return getI18n().t('SearchMenuItem.Audio', {prefix});
    case ItemType.FILE: return getI18n().t('SearchMenuItem.File', {prefix});
    case ItemType.CLIP_LIST: return getI18n().t('SearchMenuItem.ClipLists', {prefix});
    case ItemType.FOLDER: return getI18n().t('SearchMenuItem.Folders', {prefix});
    case MediaAvailabilityStatus.STREAMING: return getI18n().t('SearchMenuItem.Streaming', {prefix});
    default: return '';
  }
};

/**
 * Reset to 0, then apply search results to counts.
 */
export const typeCountsByType = (store: StoreType, list: SearchCategory[]): Record<string, number> => {
  const typeCount = store.getters.typeCount as SearchCategoryCount[];
  const mappedTypeCount = list
    .reduce<Record<string, number>>((map, cur) => {
      map[cur] = 0;
      return map;
    }, {});

  if (typeCount) {
    for (const type of typeCount) {
      mappedTypeCount[type.key] = type.doc_count;
    }
  }
  return mappedTypeCount;
};

export const hasPlusButton = (itemType: SearchCategory, canUpdateFolder: boolean, permissions: string[], doesHaveUploadPermission: boolean) => {
  return () => {
    switch (itemType) {
      case ItemType.PERSON: return canCreatePerson(permissions);
      case ItemType.FOLDER: return canCreateFolder(permissions) && canUpdateFolder;
      case ItemType.CLIP_LIST: return false;
      case MediaAvailabilityStatus.STREAMING: return canManageLiveRecording(permissions) && canUpdateFolder;
      case ItemType.VIDEO:
      case ItemType.AUDIO:
      case ItemType.IMAGE:
      case ItemType.FILE:
        return doesHaveUploadPermission && canUpdateFolder;
      default: assertUnreachable(itemType);
    }
  };
};

export function updateRadioFilterBasedOnSearchCategory(searchCategory: SearchCategory[], isAllFilter: boolean) {
  if (isAllFilter && filters.radioFilter !== ALL_FILTER) {
    setRadioFilter(ALL_FILTER);
    return;
  }

  if (filters.radioFilter === ALL_FILTER && !searchCategory.length) {
    return;
  }
  // If both media and non-media types are selected, set radio filter to all.
  if (searchCategory.length > 1 && hasBothMediaAndOtherTypes(searchCategory)) {
    if (filters.radioFilter !== ALL_FILTER as string) {
      setRadioFilter(ALL_FILTER);
    }
    return;
  }
  // Set non-media radio filter.
  if (searchCategory.length === 1 && isNonMediaType(searchCategory[0])) {
    if (filters.radioFilter !== searchCategory[0]) {
      setRadioFilter(searchCategory[0]);
    }
    return;
  }
  // If only multiple non-media types are selected, set radio filter to all.
  if (searchCategory.length > 1 && hasOnlyNonMediaTypes(searchCategory)) {
    if (filters.radioFilter !== ALL_FILTER) {
      setRadioFilter(ALL_FILTER);
    }
    return;
  }

  // If only media types are selected, set radio filter to media.
  if (searchCategory.length !== 0 && isMediaType(searchCategory[0])) {
    if (filters.radioFilter !== MEDIA_FILTER) {
      setRadioFilter(MEDIA_FILTER);
    }
    return;
  }
  // If no type selected, set radio filter to all. Though initially for no type, radio filter is set to media.
  // Here we are setting it to all.
  // If there is an existing bookmark where File name is present and no type is selected,
  // we should show the files in the search results. Since file is not part of media type we need to set radio filter to all.
  if (searchCategory.length === 0 && filters.radioFilter !== ALL_FILTER) {
    setRadioFilter(ALL_FILTER);
  }
}

